import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import {
  FONT,
  HAIRLINE,
  INK,
  INK_SECONDARY,
  STATUS,
} from "../../../theme/tokens";

function formatMemory(value, total) {
  const GIB_PER_TIB = 1024;
  if (total >= GIB_PER_TIB) {
    return `${(value / GIB_PER_TIB).toFixed(2)} TiB`;
  }
  return `${Math.round(value)} GiB`;
}

function formatCores(value) {
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return `${Math.round(value)}`;
}

function utilizationColor(percentage) {
  if (percentage >= 90) return STATUS.error;
  if (percentage >= 70) return STATUS.warning;
  return STATUS.success;
}

function formatValue(used, total, unit) {
  if (unit === "GiB") {
    return `${formatMemory(used, total)} / ${formatMemory(total, total)}`;
  }
  if (unit === "cores") {
    return `${formatCores(used)} / ${formatCores(total)} cores`;
  }
  return `${Math.round(used)} / ${Math.round(total)}${unit ? ` ${unit}` : ""}`;
}

export default function ResourceProgressBar({ title, used, total, unit }) {
  // Normalize inputs to safe numeric values to avoid NaN or runtime
  // errors when upstream data is missing or invalid.
  const safeUsed = Number.isFinite(used) ? used : 0;
  const safeTotal = Number.isFinite(total) && total > 0 ? total : 0;
  const percentage = safeTotal > 0 ? (safeUsed / safeTotal) * 100 : 0;
  const barColor = utilizationColor(percentage);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 1.5,
          mb: 0.625,
        }}
      >
        <Typography
          sx={{
            fontSize: FONT.xs,
            fontWeight: 500,
            color: INK_SECONDARY,
            letterSpacing: "0.01em",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: FONT.xs,
            fontWeight: 600,
            color: INK,
            whiteSpace: "nowrap",
            flexShrink: 0,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatValue(safeUsed, safeTotal, unit)}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 6,
          bgcolor: HAIRLINE,
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${Math.min(100, percentage)}%`,
            height: "100%",
            bgcolor: barColor,
            borderRadius: 999,
            transition: "width 240ms ease, background-color 240ms ease",
          }}
        />
      </Box>
    </Box>
  );
}

ResourceProgressBar.propTypes = {
  title: PropTypes.string.isRequired,
  used: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  unit: PropTypes.string,
};

ResourceProgressBar.defaultProps = {
  unit: "",
};
